import $RefParser from '@apidevtools/json-schema-ref-parser';
import type { TypeExpr } from '@features/flows/utils/types';

const clone = <T>(val: T): T => {
  try {
    return typeof structuredClone === 'function' ? structuredClone(val) : JSON.parse(JSON.stringify(val));
  } catch {
    return val;
  }
};

export async function dereference<T = unknown>(schema: T): Promise<T> {
  try {
    return (await $RefParser.dereference(clone(schema))) as T;
  } catch {
    return schema;
  }
}

export type FlatField = {
  path: string;
  type?: string;
  enumValues?: unknown[];
  isArray?: boolean;
};

export const normalizeName = (name: string): string => {
  return name.replace(/[_\s-]/g, '').replace(/id$/i, '').toLowerCase();
};

const visit = (
  schema: any,
  basePath: string,
  out: FlatField[],
  depth: number,
  maxFields: number,
) => {
  if (!schema || typeof schema !== 'object') return;
  if (out.length >= maxFields) return;
  const type = Array.isArray(schema.type) ? schema.type[0] : schema.type;

  if (type === 'object' || schema.properties) {
    const props = schema.properties ?? {};
    for (const [key, value] of Object.entries(props)) {
      if (out.length >= maxFields) break;
      const nextPath = basePath ? `${basePath}.${key}` : key;
      visit(value, nextPath, out, depth + 1, maxFields);
    }
    return;
  }

  if (type === 'array') {
    const prefixItems = Array.isArray((schema as any).prefixItems) ? (schema as any).prefixItems : [];
    const tupleItems = Array.isArray(schema.items) ? schema.items : [];
    const tupleSchemas = prefixItems.length ? prefixItems : tupleItems;
    if (tupleSchemas.length > 0) {
      if (basePath) {
        out.push({ path: basePath, type: 'tuple', isArray: true });
      }
      return;
    }
    if (schema.items === false) {
      if (basePath) out.push({ path: basePath, type: 'array', isArray: true });
      return;
    }
    const item = Array.isArray(schema.items) ? schema.items[0] : schema.items;
    const primitiveItemType = item && typeof item === 'object' ? (Array.isArray((item as any).type) ? (item as any).type[0] : (item as any).type) : undefined;
    const enumValues = item && typeof item === 'object' ? (item as any).enum : undefined;
    if (basePath) {
      out.push({ path: basePath, type: primitiveItemType ?? undefined, enumValues, isArray: true });
    }
    const nextPath = basePath ? `${basePath}[]` : '[]';
    visit(item ?? {}, nextPath, out, depth + 1, maxFields);
    return;
  }

  if (basePath) {
    out.push({ path: basePath, type, enumValues: schema.enum });
  }
};

export function flattenProperties(schema: unknown, opts?: { maxFields?: number }): FlatField[] {
  const out: FlatField[] = [];
  const max = Math.max(1, Math.min(2000, opts?.maxFields ?? 1000));
  visit(schema, '', out, 0, max);
  return out;
}

export function synthesizeSchemaFromTypeExpr(expr?: TypeExpr): Record<string, unknown> {
  if (!expr) return {};
  switch (expr.tag) {
    case 'any':
      return {};
    case 'coarse':
      return { type: expr.name };
    case 'array':
      return { type: 'array', items: synthesizeSchemaFromTypeExpr(expr.elem) };
    case 'tuple':
      return {
        type: 'array',
        items: expr.elems?.map((e) => synthesizeSchemaFromTypeExpr(e)) ?? {},
      };
    case 'record':
      return {
        type: 'object',
        additionalProperties: synthesizeSchemaFromTypeExpr(expr.value),
      };
    case 'shape': {
      const properties: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(expr.props)) {
        properties[key] = synthesizeSchemaFromTypeExpr(value);
      }
      return { type: 'object', properties };
    }
    case 'var':
      return {};
    default:
      return {};
  }
}

export const stripArraySuffix = (path: string): string => path.replace(/\[\]/g, '');
